/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ConfigurationType,
  DesignRuleValidationIssue,
  UpdatableConfigItem,
  ZoneItemType
} from 'data/types';
import { nanoid } from 'nanoid';
import {
  deleteFromCondensers,
  updateConfigurationHierarchy
} from './configurations-utils';

type ConfigurationState = {
  configurations: ConfigurationType[];
  configurationOptions: ConfigurationType[];
  allConfigurationOptions: ConfigurationType[];
  activeConfig: number;
  designRuleResults: DesignRuleValidationIssue[];
  configSavedAt: number;
  configSaveRequestedSeq: number;
  configSaveCompletedSeq: number;
  locallyCreatedConfigurationIds: string[];
  unsavedLocallyCreatedConfigurationIds: string[];
  protectedConfigurationIdsDuringGeneration: string[];
};

export const defaultConfigurationState: ConfigurationState = {
  configurations: [],
  configurationOptions: [],
  allConfigurationOptions: [],
  activeConfig: 0,
  designRuleResults: [],
  configSavedAt: 0,
  configSaveRequestedSeq: 0,
  configSaveCompletedSeq: 0,
  locallyCreatedConfigurationIds: [],
  unsavedLocallyCreatedConfigurationIds: [],
  protectedConfigurationIdsDuringGeneration: []
};

const configurationSlice = createSlice({
  name: 'configuration',
  initialState: defaultConfigurationState,
  reducers: {
    setConfigurations: (state, action: PayloadAction<ConfigurationType[]>) => {
      state.configurations = action.payload;
      const configurationIds = new Set(
        action.payload.map((configuration) => configuration.uniqueId)
      );
      state.locallyCreatedConfigurationIds =
        state.locallyCreatedConfigurationIds.filter((configurationUniqueId) =>
          configurationIds.has(configurationUniqueId)
        );
      state.unsavedLocallyCreatedConfigurationIds =
        state.unsavedLocallyCreatedConfigurationIds.filter(
          (configurationUniqueId) => configurationIds.has(configurationUniqueId)
        );
    },
    addLocallyCreatedConfigurationId: (
      state,
      action: PayloadAction<string>
    ) => {
      if (!state.locallyCreatedConfigurationIds.includes(action.payload)) {
        state.locallyCreatedConfigurationIds.push(action.payload);
      }
      if (
        !state.unsavedLocallyCreatedConfigurationIds.includes(action.payload)
      ) {
        state.unsavedLocallyCreatedConfigurationIds.push(action.payload);
      }
    },
    markLocallyCreatedConfigurationIdsSaved: (
      state,
      action: PayloadAction<string[]>
    ) => {
      const savedIds = new Set(action.payload);
      state.unsavedLocallyCreatedConfigurationIds =
        state.unsavedLocallyCreatedConfigurationIds.filter(
          (configurationUniqueId) => !savedIds.has(configurationUniqueId)
        );
    },
    setProtectedConfigurationIdsDuringGeneration: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.protectedConfigurationIdsDuringGeneration = action.payload;
    },
    addProtectedConfigurationIdDuringGeneration: (
      state,
      action: PayloadAction<string>
    ) => {
      if (
        !state.protectedConfigurationIdsDuringGeneration.includes(
          action.payload
        )
      ) {
        state.protectedConfigurationIdsDuringGeneration.push(action.payload);
      }
    },
    updateConfigurationItem: (
      state,
      action: PayloadAction<UpdatableConfigItem>
    ) => {
      const item = action.payload;
      const keys = Object.keys(item).filter((key) => key !== 'uniqueId');
      state.configurations.forEach((config) => {
        updateConfigurationHierarchy(config, item, keys);
      });
    },
    setConfigurationOptions: (
      state,
      action: PayloadAction<ConfigurationType[]>
    ) => {
      state.configurationOptions = action.payload;
    },
    updateConfigurationOptionItem: (
      state,
      action: PayloadAction<UpdatableConfigItem>
    ) => {
      const item = action.payload;
      const keys = Object.keys(item).filter((key) => key !== 'uniqueId');
      state.configurationOptions.forEach((configOption) => {
        updateConfigurationHierarchy(configOption, item, keys);
      });
    },
    deleteConfigurationOptionItem: (
      state,
      action: PayloadAction<{ uniqueId: string }>
    ) => {
      const { uniqueId } = action.payload;
      const configIndex = state.configurationOptions.findIndex(
        (config) => config.uniqueId === uniqueId
      );
      if (configIndex !== -1) {
        state.configurationOptions.splice(configIndex, 1);
        return;
      }
      state.configurationOptions.some((config) =>
        deleteFromCondensers(config, uniqueId)
      );
    },
    setAllConfigurationOptions: (
      state,
      action: PayloadAction<ConfigurationType[]>
    ) => {
      state.allConfigurationOptions = action.payload;
    },
    splitConfigurationOptionZoneItem: (
      state,
      action: PayloadAction<{ uniqueId: string }>
    ) => {
      const { uniqueId } = action.payload;

      const allZones = state.configurationOptions.flatMap((config) =>
        config.condensers.flatMap((condenser) => condenser.zones)
      );

      for (const zone of allZones) {
        const itemIndex = zone.zoneItems.findIndex(
          (item) => item.uniqueId === uniqueId
        );

        if (itemIndex !== -1) {
          const item = zone.zoneItems[itemIndex];
          const newPartition: ZoneItemType = {
            ...item,
            uniqueId: nanoid(5),
            isPartition: true
          };
          zone.zoneItems.splice(itemIndex, 0, newPartition);
          return;
        }
      }
    },
    setActiveConfig: (state, action: PayloadAction<number>) => {
      state.activeConfig = action.payload;
    },
    syncConfigValid: (
      state,
      action: PayloadAction<{ uniqueId: string; valid: boolean }>
    ) => {
      const config = state.configurations.find(
        (c) => c.uniqueId === action.payload.uniqueId
      );
      if (config) {
        config.valid = action.payload.valid;
      }
    },
    setDesignRuleResults: (
      state,
      action: PayloadAction<DesignRuleValidationIssue[]>
    ) => {
      state.designRuleResults = action.payload;
    },
    setConfigSavedAt: (state, action: PayloadAction<number>) => {
      state.configSavedAt = action.payload;
    },
    markConfigSaveRequested: (state, action: PayloadAction<number>) => {
      state.configSaveRequestedSeq = Math.max(
        state.configSaveRequestedSeq,
        action.payload
      );
    },
    markConfigSaveCompleted: (state, action: PayloadAction<number>) => {
      state.configSaveCompletedSeq = Math.max(
        state.configSaveCompletedSeq,
        action.payload
      );
    }
  }
});

export const selectConfigSavePending = (state: {
  configurations: ConfigurationState;
}): boolean =>
  state.configurations.configSaveRequestedSeq >
  state.configurations.configSaveCompletedSeq;

export const {
  setConfigurations,
  addLocallyCreatedConfigurationId,
  markLocallyCreatedConfigurationIdsSaved,
  setProtectedConfigurationIdsDuringGeneration,
  addProtectedConfigurationIdDuringGeneration,
  updateConfigurationItem,
  setConfigurationOptions,
  updateConfigurationOptionItem,
  deleteConfigurationOptionItem,
  setAllConfigurationOptions,
  setActiveConfig,
  splitConfigurationOptionZoneItem,
  syncConfigValid,
  setDesignRuleResults,
  setConfigSavedAt,
  markConfigSaveRequested,
  markConfigSaveCompleted
} = configurationSlice.actions;

export default configurationSlice.reducer;
